<template>
  <!-- tenant-wide default favorites bar (FI-1734). Selected chips sit inside
       a bordered box copied from od_tag_input.vue's .od-tag-input shell --
       same look as a text input full of pills -- but with no actual text
       field, since there's nothing to type: chips only get added by
       clicking one below, same click-to-insert gesture as the message
       template editors' "Available Fields" (PfsFieldHelp.vue). Reordering
       shows a moving text-caret between chips, like dropping a field chip
       into PfsRichEditor's contenteditable box does natively. Earlier this
       inserted an actual caret <span> between the chip siblings via v-if,
       but mutating the DOM under the pointer WHILE a native HTML5 drag is
       in progress makes the browser lose track of the drag target -- it
       kept snapping back to the front. The caret is now a single absolutely
       positioned overlay whose inline style just moves; the chip siblings
       never get touched mid-drag. -->
  <div class="tcfd">
    <div
      class="tcfd-box"
      ref="box"
      @dragover.prevent="onBoxDragOver"
      @drop.prevent="onDrop"
    >
      <span
        v-for="(item, idx) in orderedSelected"
        :key="item.key"
        ref="chipEls"
        class="pfs-field-chip tcfd-chip"
        :class="{ 'tcfd-chip--dragging': dragKey === item.key }"
        draggable="true"
        title="Drag to reorder"
        @dragstart="onDragStart(item.key, $event)"
        @dragover.prevent.stop="onChipDragOver(idx, $event)"
        @dragend="onDragEnd"
      >
        <i :class="'fa ' + (item.icon || 'fa-circle-o')" aria-hidden="true"></i>
        {{ item.label }}
        <button
          type="button"
          class="tcfd-chip-remove"
          title="Remove"
          :aria-label="'Remove ' + item.label"
          @click.prevent="remove(item.key)"
        >&times;</button>
      </span>
      <span v-if="!orderedSelected.length" class="tcfd-empty">
        Not set — new users get the standard default mix for their role.
      </span>
      <span v-if="dragKey && insertBeforeIndex !== null" class="tcfd-caret" :style="caretStyle"></span>
    </div>

    <div v-if="unselectedOptions.length" class="tcfd-add">
      <!-- collapsed-by-default toggle, copied verbatim from PfsFieldHelp.vue's
           "Available Fields" header (same markup/style, just its own title) -->
      <div
        style="cursor: pointer; font-size: 12px; color: #666; user-select: none; display: inline-flex; align-items: center; gap: 4px;"
        @click="expanded = !expanded"
      >
        <span>{{ expanded ? '▾' : '▸' }}</span>
        <span>Available Favorites <span style="color: #999;">{{ expanded ? '(click to add)' : '(click to expand)' }}</span></span>
      </div>
      <div v-if="expanded" style="margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px;">
        <span
          v-for="opt in unselectedOptions"
          :key="opt.key"
          class="pfs-field-chip tcfd-chip"
          @click="add(opt.key)"
        >
          <i :class="'fa ' + (opt.icon || 'fa-circle-o')" aria-hidden="true"></i>
          {{ opt.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { buildNavContext, resolveNav, navDestinations } from '@/nav/registry'
import { siteMapFavoriteDestinations } from '@/utils/site_map_pages'
import { favKey } from '@/nav/nav_favorites'

export default {
  name: 'TenantConfigFavoritesDefaults',
  props: {
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      // collapsed by default, same as PfsFieldHelp.vue's Available Fields
      expanded: false,
      // canonical key of the chip currently being dragged -- null when no
      // drag is in progress
      dragKey: null,
      // index in orderedSelected the dragged chip would land at if dropped
      // right now (0..length); null hides the caret entirely
      insertBeforeIndex: null,
      // pixel position of the caret overlay, relative to .tcfd-box -- kept
      // as plain numbers rather than derived from insertBeforeIndex so the
      // caret can sit at the exact edge of whichever chip triggered the
      // update instead of index 0 of the (possibly different) next row
      caretLeft: 0,
      caretTop: 0,
      caretHeight: 20,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    // every distinct favoritable destination this editor can offer: registry
    // first (it owns labels/icons), then the site map's wider page list for
    // anything the rail has no row for -- the same precedence resolveFavorites
    // uses when RENDERING a bar, so a chip added here is exactly what would
    // light up on a matching user's bar. built from the EDITING admin's own
    // gates: a customer-admin already has the tenant's broadest access, a
    // reasonable stand-in for "any destination this tenant could offer someone."
    allOptions() {
      if (!this.user) return []
      const ctx = buildNavContext(this.user, this.viewAs)
      const byKey = new Map()
      navDestinations(resolveNav(ctx)).forEach(dest => {
        const key = favKey(dest.to)
        if (key && !byKey.has(key)) {
          // same precedence as the toolbar chip (fi_favorites_bar.chipLabel)
          byKey.set(key, { key, label: dest.favLabel || dest.shortLabel || dest.label, icon: dest.icon })
        }
      })
      siteMapFavoriteDestinations(this.user, this.viewAs).forEach(dest => {
        const key = favKey(dest.to)
        if (key && !byKey.has(key)) {
          byKey.set(key, { key, label: dest.label, icon: dest.icon })
        }
      })
      return Array.from(byKey.values())
    },
    optionsByKey() {
      const m = new Map()
      this.allOptions.forEach(opt => m.set(opt.key, opt))
      return m
    },
    // a key already saved that this picker no longer resolves (e.g. a
    // feature since removed) still shows, keyed raw, so an admin's existing
    // list is never silently truncated out from under them
    orderedSelected() {
      const seen = new Set()
      const out = []
      ;(this.value || []).forEach(key => {
        if (seen.has(key)) return
        seen.add(key)
        out.push(this.optionsByKey.get(key) || { key, label: key, icon: null })
      })
      return out
    },
    unselectedOptions() {
      const selected = new Set(this.value || [])
      return this.allOptions.filter(opt => !selected.has(opt.key))
    },
    caretStyle() {
      return {
        left: this.caretLeft + 'px',
        top: this.caretTop + 'px',
        height: this.caretHeight + 'px',
      }
    },
  },
  methods: {
    add(key) {
      if ((this.value || []).includes(key)) return
      this.$emit('input', (this.value || []).concat([key]))
    },
    remove(key) {
      this.$emit('input', (this.value || []).filter(k => k !== key))
    },
    // drag-and-drop reordering. adapted from fi_favorites_bar.vue's chip
    // drag, but tracks a GAP (insertBeforeIndex) rather than a target chip
    // to swap with, so the caret can land on either side of whichever chip
    // the pointer is over.
    onDragStart(key, event) {
      this.dragKey = key
      event.dataTransfer.effectAllowed = 'move'
      // firefox requires data to be set for the drag to initiate at all;
      // the value itself is unused (state lives in dragKey)
      try { event.dataTransfer.setData('text/plain', key || '') } catch (_) { /* noop */ }
    },
    // which half of THIS chip the pointer is over decides whether the
    // caret goes before it or after it -- the same left/right split a text
    // caret uses to decide which side of the nearest character it's on.
    // position is read straight from the hovered chip's own rect (not
    // derived from insertBeforeIndex afterward), so the caret always lands
    // exactly at that chip's edge even when chips wrap across rows.
    onChipDragOver(idx, event) {
      if (!this.dragKey) return
      const rect = event.currentTarget.getBoundingClientRect()
      const boxRect = this.$refs.box.getBoundingClientRect()
      const isAfter = (event.clientX - rect.left) > rect.width / 2
      this.insertBeforeIndex = isAfter ? idx + 1 : idx
      this.caretLeft = (isAfter ? rect.right : rect.left) - boxRect.left
      this.caretTop = rect.top - boxRect.top
      this.caretHeight = rect.height
    },
    // fires while hovering the box's own padding/empty space (chip-level
    // handlers stopPropagation, so this only runs for gaps between/after
    // chips that aren't themselves a chip) -- treat that as "insert at the
    // end", positioned right after the last chip (or at the box's own
    // top-left corner when there are no chips yet)
    onBoxDragOver() {
      if (!this.dragKey) return
      this.insertBeforeIndex = this.orderedSelected.length
      const boxRect = this.$refs.box.getBoundingClientRect()
      const chipEls = this.$refs.chipEls || []
      const lastChip = chipEls[chipEls.length - 1]
      if (lastChip) {
        const rect = lastChip.getBoundingClientRect()
        this.caretLeft = rect.right - boxRect.left
        this.caretTop = rect.top - boxRect.top
        this.caretHeight = rect.height
      } else {
        this.caretLeft = 0
        this.caretTop = 0
        this.caretHeight = 20
      }
    },
    onDrop() {
      if (this.dragKey && this.insertBeforeIndex !== null) {
        const order = this.orderedSelected.map(i => i.key)
        const from = order.indexOf(this.dragKey)
        if (from !== -1) {
          order.splice(from, 1)
          // the removal above shifts everything after `from` left by one,
          // so a target gap past the dragged chip's old spot needs the
          // same correction
          let to = this.insertBeforeIndex
          if (from < to) to -= 1
          order.splice(to, 0, this.dragKey)
          this.$emit('input', order)
        }
      }
      this.dragKey = null
      this.insertBeforeIndex = null
    },
    onDragEnd() {
      this.dragKey = null
      this.insertBeforeIndex = null
    },
  },
}
</script>

<style scoped>
/* the selected-favorites box, copied from od_tag_input.vue's .od-tag-input
   shell -- same bordered pill-row-in-a-box look as a text input full of
   tags -- minus the trailing text field, since nothing here is typed. */
.tcfd-box {
  position: relative;
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  background: #fff; border: 1px solid #c8d4df; border-radius: 4px;
  padding: 7px 8px; min-height: 36px;
}

/* .pfs-field-chip is copied verbatim from PfsFieldHelp.vue (scoped styles
   don't cross components, so the class name alone isn't enough -- the
   rule has to come along too). that chip is a plain clickable span with
   no built-in remove affordance, so .tcfd-chip only adds what's needed to
   fit it into the removable box above. */
.pfs-field-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  background: #e8f0fe;
  border: 1px solid #c5d5f7;
  border-radius: 12px;
  font-family: monospace;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  color: #1a56db;
  transition: background 0.15s;
  /* flex items shrink below their content size by default, which (with no
     white-space:nowrap) makes the icon/label/remove-button wrap onto their
     own lines inside a single squeezed chip once enough chips overflow one
     row -- flex: 0 0 auto keeps each chip at its natural width and lets
     .tcfd-box's flex-wrap push the overflow to a new row instead. */
  flex: 0 0 auto;
}
.pfs-field-chip:hover {
  background: #c5d5f7;
}
/* only the selected chips (inside the box) are draggable -- the "Available
   Favorites" ones below share the same base chip class but stay a plain
   pointer, since clicking is all they do */
.tcfd-box .pfs-field-chip { cursor: grab; }

/* remove button. a solid filled circle rather than a bare glyph, since a
   thin outline "x" in the same blue as the chip text/border all but
   disappeared against the light-blue chip background */
.tcfd-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #1a56db;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
}
.tcfd-chip-remove:hover { background: #123c9e; }

/* dragged chip stays in place but fades, so its slot in the box still
   reads as "this one is moving" rather than just vanishing -- same recipe
   as fi_favorites_bar.vue's .fi-fb-chip-wrap--dragging */
.tcfd-chip--dragging { opacity: 0.4; }

/* the insertion-point caret: a blinking text-cursor-style bar that sits in
   the exact gap the dragged chip would land in, same visual metaphor as
   the native caret you get for free when dragging a field chip into
   PfsRichEditor's contenteditable box. absolutely positioned (left/top set
   inline via caretStyle) rather than a real sibling of the chips, so
   moving it never touches the chip elements' own DOM nodes mid-drag.
   pointer-events: none keeps it from ever intercepting its own dragover. */
.tcfd-caret {
  position: absolute;
  width: 2px;
  background: #1a56db;
  border-radius: 1px;
  pointer-events: none;
  animation: tcfd-caret-blink 0.85s step-end infinite;
}
@keyframes tcfd-caret-blink {
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0.15; }
}

.tcfd-empty {
  color: #888;
  font-style: italic;
  font-size: 13px;
}

.tcfd-add {
  margin-top: 10px;
}
</style>
